/* ==========================================================================
   ux-product-slider — a press on either arrow takes the row to that end.

   Loaded only from sections/ux-product-slider.liquid, which renders the row
   only when it has products.

   The row's own options already make a press mean something: `groupCells`
   groups the cards into the pages its cell fits, so the arrows step pages
   rather than cards, the last page *is* the row's end, and the theme's own
   index-based disabled state is right at both ends — no arithmetic of ours is
   needed for it. What is left is the press itself: an arrow here is "take me
   to that end of the line-up", one press however many pages the row has, and
   the theme's handler steps a single page. So this reads the press on the way
   down — a capture listener on the section, ahead of the button's own — and
   sends Flickity to its last (or first) slide, which `contain` lands flush at
   the row's end. Left and right arrows do the same while the row itself has
   the focus, so the keys and the buttons agree; `event.target === slider` is
   the case Flickity's own keyboard support is written for.

   If this file never arrives, nothing is broken: the arrows step one page at a
   time and stop at the row's end with the theme's own state, which for a row
   of the frames' six cards is two pages — the end on the first press anyway.

   Nothing else about the slider is touched — the buttons, their labels, their
   disabled state and styling, and the sliding itself stay the theme's.

   Teardown: the DOM listeners come off on `shopify:section:unload` and on the
   element leaving the document, so the theme editor can re-render the section
   as often as it likes.
   ========================================================================== */

(function () {
  'use strict';

  const SLIDER = 'slider-component';
  const PREV = '.flickity-prev-next-button.previous';
  const NEXT = '.flickity-prev-next-button.next';
  const ROOT = '[data-section-type="ux-product-slider"]';

  /* One entry per rendered section, so the editor can take a section's
     listeners down without touching another's. */
  const wired = new WeakMap();

  function jump(slider, toEnd) {
    const slides = slider.flkty?.slides;

    if (!slides?.length) return;

    slider.flkty.select(toEnd ? slides.length - 1 : 0);
  }

  function onPress(slider) {
    return (event) => {
      const button = event.target.closest(`${PREV}, ${NEXT}`);

      if (!button || button.disabled) return;

      /* Ahead of the theme's own handler on the button, so its single page
         step never starts. */
      event.stopPropagation();
      event.preventDefault();

      jump(slider, button.matches(NEXT));
    };
  }

  function onKeydown(slider) {
    return (event) => {
      if (event.target !== slider) return;

      const toEnd = event.key === 'ArrowRight';
      const toStart = event.key === 'ArrowLeft';

      if (!toEnd && !toStart) return;

      event.stopPropagation();
      event.preventDefault();

      jump(slider, toEnd);
    };
  }

  function stop(root) {
    const entry = wired.get(root);

    if (!entry) return;

    entry.unwatch();

    wired.delete(root);
  }

  function start(root) {
    if (wired.has(root)) return;

    const slider = root.querySelector(SLIDER);

    if (!slider) return;

    const wire = () => {
      if (!slider.flkty || wired.has(root)) return;

      const press = onPress(slider);
      const keydown = onKeydown(slider);

      root.addEventListener('click', press, true);
      root.addEventListener('keydown', keydown, true);

      wired.set(root, {
        unwatch: () => {
          root.removeEventListener('click', press, true);
          root.removeEventListener('keydown', keydown, true);
        },
      });
    };

    /* The theme's slider-component reports readiness, and has usually been
       ready before this file runs; either order is covered. */
    if (slider.flkty) {
      wire();
    } else {
      root.addEventListener('theme:slider:loaded', wire, { once: true });
    }
  }

  function roots(scope) {
    return Array.from(scope.querySelectorAll(ROOT));
  }

  document.addEventListener('shopify:section:load', (event) => {
    roots(event.target).forEach(start);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    roots(event.target).forEach(stop);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => roots(document).forEach(start), { once: true });
  } else {
    roots(document).forEach(start);
  }
})();
